import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from './StatusBadge';
import { Logo } from './Logo';
import { UserRole } from '../../types/auth';

interface HeaderProps {
    showBackButton?: boolean;
    backButtonText?: string;
    backButtonPath?: string;
    title?: string;
}

export const Header: React.FC<HeaderProps> = ({
    showBackButton = false,
    backButtonText = 'Назад',
    backButtonPath = '/client/dashboard',
    title
}) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Закрытие выпадающего списка при клике вне его
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getRoleDisplay = (role: UserRole) => {
        switch (role) {
            case UserRole.CLIENT:
                return 'Клиент';
            case UserRole.MODERATOR:
                return 'Модератор';
            case UserRole.SIGNATORY:
                return 'Подписант';
            case UserRole.SUPER_ADMIN:
                return 'Супер-администратор';
            default:
                return role;
        }
    };

    const handleBackClick = () => {
        navigate(backButtonPath);
    };

    const handleLogoClick = () => {
        navigate('/client/dashboard');
    };


    const handleProfileClick = () => {
        navigate('/profile');
        setIsDropdownOpen(false);
    };

    const handleLogoutClick = () => {
        logout();
        setIsDropdownOpen(false);
    };

    if (!user) return null;

    return (
        <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-4">
                    {/* Left side - Logo and title */}
                    <div className="flex items-center space-x-4">
                        <div
                            className="flex items-center cursor-pointer hover:opacity-80"
                            onClick={handleLogoClick}
                        >
                            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center mr-3 overflow-hidden p-1">
                                <Logo size="md" className="w-full h-full rounded-full" />
                            </div>
                            <h1 className="text-xl font-bold text-gray-900">Система "AISER"</h1>
                        </div>

                        {title && (
                            <>
                                <div className="text-gray-400">/</div>
                                <h2 className="text-lg font-medium text-gray-700">{title}</h2>
                            </>
                        )}
                    </div>

                    {/* Center - Back button */}
                    {showBackButton && (
                        <div className="flex-1 flex justify-center">
                            <button
                                onClick={handleBackClick}
                                className="btn-secondary flex items-center"
                            >
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                                {backButtonText}
                            </button>
                        </div>
                    )}

                    {/* Right side - User avatar with dropdown - скрыт */}
                    {/* <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => {
                                console.log('Button clicked! Current state:', isDropdownOpen);
                                setIsDropdownOpen(!isDropdownOpen);
                            }}
                            className={`flex items-center space-x-3 p-2 rounded-lg transition-colors cursor-pointer ${isDropdownOpen ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                            style={{ border: '2px solid red' }} // Временная граница для отладки
                        >
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>

                            <div className="text-left">
                                <p className="text-sm font-medium text-gray-900">{user.fullName}</p>
                                <p className="text-xs text-gray-500">{getRoleDisplay(user.role)}</p>
                            </div>

                            <StatusBadge status={user.status} />

                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isDropdownOpen && (
                            <div
                                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
                                style={{ border: '2px solid blue' }} // Временная граница для отладки
                            >
                                <button
                                    onClick={handleProfileClick}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                >
                                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Профиль
                                </button>
                                <button
                                    onClick={handleLogoutClick}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                >
                                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    Выйти
                                </button>
                            </div>
                        )}
                    </div> */}
                </div>
            </div>
        </header>
    );
};
